import {
  type FunctionComponent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Button, Input, Link } from '@dr.pogodin/react-utils';

import TypewriterText from 'components/TypewriterText';
import { Listening, TypingIndicator } from 'components/ai-app';
import type { StateT } from 'hooks/useWellbeingAgent';
import { getRealtimeAiClient } from 'services';
import {
  type InsightT,
  type InsightsMapT,
  type TranscriptT,
  findById,
} from 'utils';

import IconTypeMode from 'assets/images/ai/icon-type-mode.svg';
import IconMicrophone from 'assets/images/icon-microphone-32px.svg';
import IconSend from 'assets/images/icon-send.svg';

import badgeListeningTheme from 'themes/Listening/badge.scss';

import { Insight } from '../../Insight';

import { RightPanel } from '../RightPanel';

import { Message } from './Message';

import buttonTheme from './button.scss';
import inputTheme from './input.scss';

import './style.scss';

export type InputModeT = 'keyboard' | 'mic';

function insightNodes(list: InsightT[]): ReactNode[] {
  return list.map((insightItem) => (
    <Insight
      category={insightItem.category}
      key={`${insightItem.category}/${insightItem.insight}`}
    >
      {insightItem.insight}
    </Insight>
  ));
}

function aggregateInsights(insights: InsightsMapT): InsightT[] {
  let result: InsightT[] = [];
  for (const array of Object.values(insights)) {
    result = result.concat(array);
  }
  return result;
}

function addInsightsPanel(
  content: ReactNode[],
  hideInsights: boolean | undefined,
  insights: InsightsMapT,
): void {
  if (!hideInsights) {
    content.push(
      <RightPanel insights={aggregateInsights(insights)} key="insights" />,
    );
  }
}

type PropsT = {
  agentState: StateT;
  hideInsights?: boolean;
  initialInputMode: InputModeT;
  insights: InsightsMapT;
  noAudioInputAfterMessageId?: string;
  transcript: TranscriptT;
};

export const LeftPanel: FunctionComponent<PropsT> = ({
  agentState,
  hideInsights,
  initialInputMode,
  insights,
  noAudioInputAfterMessageId,
  transcript,
}) => {
  const [inputMode, setInputMode] = useState<InputModeT>(initialInputMode);

  const mutedOnStart = useRef(false);

  useEffect(() => {
    if (
      initialInputMode === 'keyboard'
      && agentState !== 'INITIALIZATION'
      && !mutedOnStart.current
    ) {
      mutedOnStart.current = true;
      void (async () => {
        const rt = await getRealtimeAiClient();
        void rt.mute();
      })();
    }
  }, [initialInputMode, agentState]);

  const viewportRef = useRef<HTMLDivElement>(null);

  const scrollToEnd = () => {
    const view = viewportRef.current;
    view?.scrollTo(0, view.scrollHeight);
  };

  // NOTE: This keeps the content scrolled to the end once the messages
  // fill up the viewport, appearing from the top edge.
  useEffect(scrollToEnd);

  const content: ReactNode[] = [];
  if (agentState === 'INITIALIZATION') {
    content.push(
      <Message key="initialization" onGrowthH={scrollToEnd} source="AI">
        <TypingIndicator />
      </Message>,
    );
  } else if (agentState === 'FAILED') {
    content.push(
      <Message key="failed" onGrowthH={scrollToEnd} source="SYSTEM_ERROR">
        Check-in session failed
      </Message>,
    );
  }

  for (const item of transcript) {
    // TODO: If we keep <TypewriterText> effect, alongside the current layout
    // logic, then we need to watch & scroll to the end of the chat viewport
    // every time any <TypewriterText> component changes (grows) its height,
    // as in the result it may shift the bottom of the content beyond
    // the currently visible viewport bounds.
    content.push(
      <Message
        key={item.id}
        onGrowthH={scrollToEnd}
        source={item.role === 'assistant' ? 'AI' : 'USER'}
      >
        {item.message || item.role === 'user'
          ? <TypewriterText>{item.message || '...'}</TypewriterText>
          : <TypingIndicator />}
      </Message>,
    );

    if (item.id === noAudioInputAfterMessageId) {
      content.push(
        <Message
          key="no-audio-input-warning"
          onGrowthH={scrollToEnd}
          source="SYSTEM_WARNING"
        >
          Still there? You can also type your response.
        </Message>,
      );
    }

    const ins = insights[item.id];
    if (ins) {
      content.push(
        // TODO: Should we animate the entrance of these insights, as in the AI
        // generated design, or not now?
        <div key={`insights/${item.id}`} styleName="insights">
          {insightNodes(ins)}
        </div>,
      );
    }
  }

  // Insights whose item is gone (a reply superseded by a continued turn), or
  // captured before any item existed.
  const orphaned = Object.entries(insights)
    .filter(([id]) => !findById(transcript, id))
    .flatMap(([, list]) => list);
  if (orphaned.length) {
    content.push(
      <div key="insights/orphaned" styleName="insights">
        {insightNodes(orphaned)}
      </div>,
    );
  }

  if (inputMode === 'mic') {
    if (agentState === 'LISTENING' || agentState === 'LISTENING-AND-HEARING') {
      content.push(
        <Message
          key="listening-indicator"
          onGrowthH={scrollToEnd}
          source="LISTENING"
        >
          <Listening mini theme={badgeListeningTheme} />
        </Message>,
      );
    }
  }

  addInsightsPanel(content, hideInsights, insights);

  let footer: ReactNode;

  const [text, setText] = useState('');

  const postText = async () => {
    const rt = await getRealtimeAiClient();
    await rt.sendMessage(text);
    setText('');
  };

  const switchToKeyboard = async () => {
    setText('');
    const rt = await getRealtimeAiClient();
    void rt.mute();
    setInputMode('keyboard');
  };

  const [handled, setHandled] = useState<string>();

  if (noAudioInputAfterMessageId && noAudioInputAfterMessageId !== handled) {
    setHandled(noAudioInputAfterMessageId);
    if (inputMode !== 'keyboard') void switchToKeyboard();
  }

  switch (inputMode) {
    case 'keyboard':
      footer = (
        <div styleName="footer">
          <div styleName="inputBar">
            <Link
              onClick={() => {
                void (async () => {
                  const rt = await getRealtimeAiClient();
                  void rt.unmute();
                  setInputMode('mic');
                })();
              }}
              styleName="iconButton"
              to=""
            >
              <IconMicrophone styleName="modeIcon" />
            </Link>
            <Input
              onChange={(e) => {
                setText(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void postText();
              }}
              theme={inputTheme}
              value={text}
            />
            <Button
              disabled={!text}
              onClick={() => {
                void postText();
              }}
              theme={buttonTheme}
            >
              <IconSend styleName="sendIcon" />
            </Button>
          </div>
        </div>
      );
      break;
    case 'mic':
      footer = (
        <div styleName="footer">
          <div styleName="inputBar">
            <Link
              onClick={() => {
                void switchToKeyboard();
              }}
              styleName="iconButton"
              to=""
            >
              <IconTypeMode styleName="modeIcon" />
            </Link>
            <div styleName="spacer" />
            <div styleName="listeningBadge">
              <Listening mini theme={badgeListeningTheme} />
            </div>
          </div>
        </div>
      );
      break;
    default: throw Error('Unexpected input mode');
  }

  return (
    <div styleName="leftPanel">
      <div ref={viewportRef} styleName="mainContent">
        {content}
      </div>
      {footer}
    </div>
  );
};
